import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
	Box,
	Button,
	Card,
	CardContent,
	TextField,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	FormControlLabel,
	Switch,
	Chip,
	Typography,
	Alert,
	CircularProgress,
	IconButton,
} from "@mui/material";
import { CloudUpload, Add, Close, ArrowBack } from "@mui/icons-material";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { createProject, updateProject, getProject } from "../api/projects";
import { IProject } from "../types/project";

// Validation schema
const projectSchema = z.object({
	title: z.object({
		en: z.string().min(1, "English title is required"),
		ru: z.string().min(1, "Russian title is required"),
	}),
	description: z.object({
		en: z.string().min(1, "English description is required"),
		ru: z.string().min(1, "Russian description is required"),
	}),
	category: z.string().refine((val) => ["frontend", "mobile", "fullstack", "backend"].includes(val), {
		message: "Category must be one of: frontend, mobile, fullstack, backend",
	}),
	demoUrl: z.string().refine((val) => val === "" || /^https?:\/\/.+/.test(val), {
		message: "Demo URL must be a valid URL or empty",
	}),
	featured: z.boolean(),
	technologies: z.array(z.string()).min(1, "At least one technology is required"),
});

type ProjectFormData = z.infer<typeof projectSchema>;

const categories = [
	{ value: "frontend", label: "Frontend" },
	{ value: "mobile", label: "Mobile" },
	{ value: "fullstack", label: "Full Stack" },
	{ value: "backend", label: "Backend" },
];

export default function CreateProjectPage() {
	const navigate = useNavigate();
	const { id } = useParams<{ id: string }>();
	const queryClient = useQueryClient();
	const [imageFile, setImageFile] = useState<File | null>(null);
	const [imagePreview, setImagePreview] = useState<string | null>(null);
	const [newTechnology, setNewTechnology] = useState("");

	const isEditing = Boolean(id);

	// Fetch project data if editing
	const { data: existingProject, isLoading: isLoadingProject } = useQuery({
		queryKey: ["project", id],
		queryFn: () => getProject(id!),
		enabled: isEditing,
	});

	const {
		control,
		handleSubmit,
		formState: { errors, isSubmitting },
		reset,
		watch,
		setValue,
	} = useForm<ProjectFormData>({
		resolver: zodResolver(projectSchema),
		defaultValues: {
			title: { en: "", ru: "" },
			description: { en: "", ru: "" },
			category: "frontend",
			demoUrl: "",
			featured: false,
			technologies: [],
		},
	});

	const watchedTechnologies = watch("technologies");

	// Reset form when existing project data is loaded
	useEffect(() => {
		if (existingProject) {
			reset({
				title: existingProject.title,
				description: existingProject.description,
				category: existingProject.category,
				demoUrl: existingProject.demoUrl || "",
				featured: existingProject.featured || false,
				technologies: existingProject.technologies,
			});
			if (existingProject.imageUrl) {
				setImagePreview(`${process.env.REACT_APP_API_URL}/${existingProject.imageUrl}`);
			}
		}
	}, [existingProject, reset]);

	// Create/Update mutation
	const mutation = useMutation({
		mutationFn: async (data: ProjectFormData) => {
			const formData = new FormData();

			// Add form fields
			formData.append("title", JSON.stringify(data.title));
			formData.append("description", JSON.stringify(data.description));
			formData.append("category", data.category);
			formData.append("featured", data.featured.toString());
			formData.append("technologies", JSON.stringify(data.technologies));

			if (data.demoUrl) {
				formData.append("demoUrl", data.demoUrl);
			}

			// Add image if selected
			if (imageFile) {
				formData.append("image", imageFile);
			}

			if (isEditing) {
				return updateProject(id!, formData);
			} else {
				return createProject(formData);
			}
		},
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["projects"] });
			navigate("/dashboard/projects");
		},
	});

	const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0];
		if (file) {
			setImageFile(file);
			const reader = new FileReader();
			reader.onload = (e) => {
				setImagePreview(e.target?.result as string);
			};
			reader.readAsDataURL(file);
		}
	};

	const handleAddTechnology = () => {
		if (newTechnology.trim() && !watchedTechnologies.includes(newTechnology.trim())) {
			setValue("technologies", [...watchedTechnologies, newTechnology.trim()]);
			setNewTechnology("");
		}
	};

	const handleRemoveTechnology = (techToRemove: string) => {
		setValue(
			"technologies",
			watchedTechnologies.filter((tech) => tech !== techToRemove)
		);
	};

	const onSubmit = (data: ProjectFormData) => {
		mutation.mutate(data);
	};

	if (isLoadingProject) {
		return (
			<Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
				<CircularProgress />
			</Box>
		);
	}

	return (
		<Box sx={{ p: 3, maxWidth: 1200, mx: "auto" }}>
			<Box sx={{ mb: 3, display: "flex", alignItems: "center", gap: 2 }}>
				<IconButton onClick={() => navigate("/dashboard/projects")}>
					<ArrowBack />
				</IconButton>
				<Typography variant="h4" component="h1">
					{isEditing ? "Edit Project" : "Create New Project"}
				</Typography>
			</Box>

			{mutation.isError && (
				<Alert severity="error" sx={{ mb: 3 }}>
					{mutation.error?.message || "An error occurred while saving the project"}
				</Alert>
			)}

			<form onSubmit={handleSubmit(onSubmit)}>
				<Box sx={{ display: "flex", gap: 3, flexDirection: { xs: "column", md: "row" } }}>
					{/* Left Column - Form Fields */}
					<Box sx={{ flex: { md: 2 } }}>
						<Card>
							<CardContent>
								<Typography variant="h6" gutterBottom>
									Project Information
								</Typography>

								{/* Title Fields */}
								<Box sx={{ display: "flex", gap: 2, mb: 3, flexDirection: { xs: "column", md: "row" } }}>
									<Box sx={{ flex: 1 }}>
										<Controller
											name="title.en"
											control={control}
											render={({ field }) => (
												<TextField
													{...field}
													label="Title (English)"
													fullWidth
													error={!!errors.title?.en}
													helperText={errors.title?.en?.message}
												/>
											)}
										/>
									</Box>
									<Box sx={{ flex: 1 }}>
										<Controller
											name="title.ru"
											control={control}
											render={({ field }) => (
												<TextField
													{...field}
													label="Title (Russian)"
													fullWidth
													error={!!errors.title?.ru}
													helperText={errors.title?.ru?.message}
												/>
											)}
										/>
									</Box>
								</Box>

								{/* Description Fields */}
								<Box sx={{ display: "flex", gap: 2, mb: 3, flexDirection: { xs: "column", md: "row" } }}>
									<Box sx={{ flex: 1 }}>
										<Controller
											name="description.en"
											control={control}
											render={({ field }) => (
												<TextField
													{...field}
													label="Description (English)"
													fullWidth
													multiline
													rows={4}
													error={!!errors.description?.en}
													helperText={errors.description?.en?.message}
												/>
											)}
										/>
									</Box>
									<Box sx={{ flex: 1 }}>
										<Controller
											name="description.ru"
											control={control}
											render={({ field }) => (
												<TextField
													{...field}
													label="Description (Russian)"
													fullWidth
													multiline
													rows={4}
													error={!!errors.description?.ru}
													helperText={errors.description?.ru?.message}
												/>
											)}
										/>
									</Box>
								</Box>

								{/* Category and Demo URL */}
								<Box sx={{ display: "flex", gap: 2, mb: 3, flexDirection: { xs: "column", md: "row" } }}>
									<Box sx={{ flex: 1 }}>
										<Controller
											name="category"
											control={control}
											render={({ field }) => (
												<FormControl fullWidth error={!!errors.category}>
													<InputLabel>Category</InputLabel>
													<Select {...field} label="Category">
														{categories.map((category) => (
															<MenuItem key={category.value} value={category.value}>
																{category.label}
															</MenuItem>
														))}
													</Select>
												</FormControl>
											)}
										/>
									</Box>
									<Box sx={{ flex: 1 }}>
										<Controller
											name="demoUrl"
											control={control}
											render={({ field }) => (
												<TextField
													{...field}
													label="Demo URL (optional)"
													fullWidth
													error={!!errors.demoUrl}
													helperText={errors.demoUrl?.message}
												/>
											)}
										/>
									</Box>
								</Box>

								{/* Featured Switch */}
								<Box sx={{ mb: 3 }}>
									<Controller
										name="featured"
										control={control}
										render={({ field }) => (
											<FormControlLabel
												control={
													<Switch
														checked={field.value}
														onChange={field.onChange}
													/>
												}
												label="Featured Project"
											/>
										)}
									/>
								</Box>

								{/* Technologies */}
								<Box sx={{ mb: 3 }}>
									<Typography variant="h6" gutterBottom>
										Technologies
									</Typography>
									<Box sx={{ display: "flex", gap: 1, mb: 2 }}>
										<TextField
											value={newTechnology}
											onChange={(e) => setNewTechnology(e.target.value)}
											placeholder="Add technology"
											size="small"
											onKeyPress={(e) => {
												if (e.key === "Enter") {
													e.preventDefault();
													handleAddTechnology();
												}
											}}
										/>
										<Button
											variant="outlined"
											onClick={handleAddTechnology}
											startIcon={<Add />}
										>
											Add
										</Button>
									</Box>
									<Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
										{watchedTechnologies.map((tech) => (
											<Chip
												key={tech}
												label={tech}
												onDelete={() => handleRemoveTechnology(tech)}
												deleteIcon={<Close />}
											/>
										))}
									</Box>
									{errors.technologies && (
										<Typography color="error" variant="caption">
											{errors.technologies.message}
										</Typography>
									)}
								</Box>
							</CardContent>
						</Card>
					</Box>

					{/* Right Column - Image Upload */}
					<Box sx={{ flex: { md: 1 } }}>
						<Card>
							<CardContent>
								<Typography variant="h6" gutterBottom>
									Project Image
								</Typography>

								<Box
									sx={{
										border: "2px dashed #ccc",
										borderRadius: 2,
										p: 3,
										textAlign: "center",
										mb: 2,
										minHeight: 200,
										display: "flex",
										flexDirection: "column",
										justifyContent: "center",
										alignItems: "center",
									}}
								>
									{imagePreview ? (
										<>
											<img
												src={imagePreview}
												alt="Preview"
												style={{
													maxWidth: "100%",
													maxHeight: 200,
													objectFit: "cover",
													borderRadius: 8,
												}}
											/>
											<Button
												variant="outlined"
												onClick={() => {
													setImageFile(null);
													setImagePreview(null);
												}}
												sx={{ mt: 2 }}
											>
												Remove Image
											</Button>
										</>
									) : (
										<>
											<CloudUpload sx={{ fontSize: 48, color: "text.secondary", mb: 2 }} />
											<Typography variant="body2" color="text.secondary" gutterBottom>
												Upload project image
											</Typography>
											<Button
												variant="outlined"
												component="label"
												startIcon={<CloudUpload />}
											>
												Choose File
												<input
													type="file"
													hidden
													accept="image/*"
													onChange={handleImageChange}
												/>
											</Button>
										</>
									)}
								</Box>

								<Typography variant="caption" color="text.secondary">
									Recommended size: 800x600px. Max file size: 5MB.
								</Typography>
							</CardContent>
						</Card>
					</Box>
				</Box>

				{/* Submit Buttons */}
				<Box sx={{ mt: 3, display: "flex", gap: 2, justifyContent: "flex-end" }}>
					<Button
						variant="outlined"
						onClick={() => navigate("/projects")}
						disabled={isSubmitting}
					>
						Cancel
					</Button>
					<Button
						type="submit"
						variant="contained"
						disabled={isSubmitting}
						startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
					>
						{isSubmitting ? "Saving..." : isEditing ? "Update Project" : "Create Project"}
					</Button>
				</Box>
			</form>
		</Box>
	);
}
